/**
 * SingleSelectDropdown Component
 * Reusable single-select dropdown built on shadcn Select
 */

'use client'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './Select'
import { cn } from '@/lib/utils'

interface DropdownOption {
  value: string
  label: string
  fontFamily?: string // Optional font family for displaying option in its font
}

interface SingleSelectDropdownProps {
  label?: string
  value: string
  options: DropdownOption[]
  onChange: (value: string) => void
  showLabel?: boolean
  className?: string
  triggerClassName?: string
  error?: boolean
  placeholder?: string
  disabled?: boolean
}

export default function SingleSelectDropdown({
  label,
  value,
  options,
  onChange,
  showLabel = true,
  className = '',
  triggerClassName = '',
  error = false,
  placeholder = 'Select an option',
  disabled = false,
}: SingleSelectDropdownProps) {
  const selectedOption = options.find((opt) => opt.value === value)

  return (
    <div className={cn('relative w-full', className)}>
      {showLabel && label && (
        <label className="block text-xs sm:text-sm font-medium text-muted-foreground mb-1.5">
          {label}
        </label>
      )}
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          className={cn(
            error && 'border-destructive focus:ring-destructive',
            !showLabel && !label && 'h-10 sm:h-9',
            triggerClassName
          )}
        >
          <SelectValue placeholder={placeholder}>
            {selectedOption && (
              <span
                style={
                  selectedOption.fontFamily
                    ? { fontFamily: selectedOption.fontFamily }
                    : undefined
                }
              >
                {selectedOption.label}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              fontFamily={option.fontFamily}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
